import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

/**
 * Map a pattern failure to a custom error key.
 * Pass a full-match regex (add ^...$ yourself if needed).
 */
export function patternKey(regex: RegExp, key: string): ValidatorFn {
  const base = Validators.pattern(regex);
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (v == null || v === '') return null; // let "required" handle empties
    const err = base(control);              // null = ok, object = fail
    return err ? { [key]: true } : null;    // <-- correct direction
  };
}


/** Forbid a value that equals a specific string (case-insensitive by default). */
export function forbidExact(word: string, key = 'forbiddenExact', opts: { caseInsensitive?: boolean } = { caseInsensitive: true }): ValidatorFn {
   const cmp = opts.caseInsensitive ? word.toLowerCase() : word;
   return (control: AbstractControl): ValidationErrors | null => {
      const v: string = control.value;
      if (!v) return null;
      const same = opts.caseInsensitive ? v.toLowerCase() === cmp : v === cmp;
      return same ? { [key]: true } : null;
   };
}

/** Forbid any of a list (strings or regexes). Uses OR logic. */
export function forbidAnyOf(tests: Array<string | RegExp>, key = 'forbiddenAny'): ValidatorFn {
   const norm = tests.map(t => (typeof t === 'string' ? new RegExp(`^${escapeRegExp(t)}$`, 'i') : t));
   return (control: AbstractControl): ValidationErrors | null => {
      const v: string = control.value;
      if (!v) return null;
      return norm.some(r => r.test(v)) ? { [key]: true } : null;
   };
}

/** No Cyrillic or extended Latin (adjust ranges if needed). */
export function noCyrillicOrExtendedLatin(key = 'noCyrillic'): ValidatorFn {
  const rx = /^(?!.*[\u0400-\u04FF\u0100-\u017F]).*$/;
  return patternKey(rx, key);
}


// --- helpers ---
function escapeRegExp(s: string) {
   return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
export function needUpper(key = 'needUpper'): ValidatorFn {
   const regex = /[A-Z]/;
   return (control: AbstractControl): ValidationErrors | null => {
      const v: string = control.value;
      if (!v) return null; // let "required" handle empty
      return regex.test(v) ? null : { [key]: true };
   };
}

// Require at least one digit
export function needDigit(key = 'needDigit'): ValidatorFn {
   const regex = /\d/;
   return (control: AbstractControl): ValidationErrors | null => {
      const v: string = control.value;
      if (!v) return null;
      return regex.test(v) ? null : { [key]: true };
   };
}
// Require at least one special character
export function needSpecialChar(key = 'needSpecialChar'): ValidatorFn {
   const regex = /[!@#$%^&*(),.?":{}|<>]/;
   return (control: AbstractControl): ValidationErrors | null => {
      const v: string = control.value;
      if (!v) return null;
      return regex.test(v) ? null : { [key]: true };
   };
}


export function atLeastOneUpper(key = 'atLeastOneUpper'): ValidatorFn {
  const regex = /[A-Z]/;
  return (control: AbstractControl): ValidationErrors | null => {
    const v: string = control.value;
    if (!v) return null; // skip empty, let "required" handle
    return regex.test(v) ? null : { [key]: true };
  };
}

export function atLeastOneLower(key = 'atLeastOneLower'): ValidatorFn {
  const regex = /[a-z]/;
  return (control: AbstractControl): ValidationErrors | null => {
    const v: string = control.value;
    if (!v) return null;
    return regex.test(v) ? null : { [key]: true };
  };
}